import {useLocation, useNavigate} from "react-router";
import {useApi} from "./api.hook";
import {useAddress} from "../components/addressManagement/addressManagement";
import {useCallback, useEffect, useMemo, useState} from "react";
import {activateNotification} from "../utils/notifications-utils";
import {Mixpanel} from "../utils/mixpanel";
import {useCart} from "../providers/cart.provider";
import {useAuth} from "../providers/authentication.provider";

const CHECKOUT_DATA_STORAGE_KEY = "checkout_data";

const getStoredCheckoutData = () => {
    try {
        const storedCheckoutData = sessionStorage.getItem(CHECKOUT_DATA_STORAGE_KEY);
        return storedCheckoutData ? JSON.parse(storedCheckoutData) : null;
    } catch {
        return null;
    }
};

const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
};

export const useReviewAndCheckout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const api = useApi();
    const {

        isAddressModalOpen,
        setIsAddressModalOpen,
        toggleAddressModal: toggleAuthenticatedUserAddressModal,
    } = useAddress();
    const { user, isGuestUser, isAuthenticated } = useAuth();
    const { handleCheckout, cartItems } = useCart();

    const [checkoutData, setCheckoutData] = useState(
        () => location.state || getStoredCheckoutData()
    );
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
    const [appliedReferralCode, setAppliedReferralCode] = useState(null);
    const [isPickup, setIsPickup] = useState(false);
    const [pickupState, setPickupState] = useState("");
    const [pickupLocations, setPickupLocations] = useState([]);
    const [pickupLoading, setPickupLoading] = useState(false);
    const [pickupError, setPickupError] = useState(null);
    const [selectedPickupLocationId, setSelectedPickupLocationId] = useState("");
    const [autoPickupInitialized, setAutoPickupInitialized] = useState(false);
    const [orderNotes, setOrderNotes] = useState("");
    const [staffEmail, setStaffEmail] = useState("");
    const isStaffCheckout = location.pathname === "/cart/checkout/staff";

    const {
        combined_split_buys = [],
        split_buyer_address = null,
        payment_url = null,
        cost_breakdown = [],
        discount_applied,
    } = checkoutData || {};

    const displayName = useMemo(() => {
        if (user?.firstname || user?.lastname) {
            return `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim();
        }
        return selectedAddress?.name || "Not specified";
    }, [user, selectedAddress]);

    const availablePickupStates = useMemo(() => {
        const items = cartItems || [];
        if (!items.length) return [];

        const normalize = (value) => String(value).trim().toLowerCase();
        const getItemStates = (item) => {
            const choices = item?.choice_of_delivery_locations;
            if (Array.isArray(choices) && choices.length) {
                return choices.map((state) => String(state).trim()).filter(Boolean);
            }
            if (item?.location) {
                return String(item.location)
                    .split(",")
                    .map((part) => part.trim())
                    .filter(Boolean);
            }
            return [];
        };

        const firstStates = getItemStates(items[0]);
        if (!firstStates.length) return [];

        let intersection = firstStates.map(normalize);
        for (let i = 1; i < items.length; i += 1) {
            const states = getItemStates(items[i]);
            if (!states.length) return [];
            const normalized = states.map(normalize);
            intersection = intersection.filter((state) => normalized.includes(state));
            if (!intersection.length) return [];
        }

        const originalMap = new Map(
            firstStates.map((state) => [normalize(state), state])
        );
        return Array.from(new Set(intersection))
            .map((state) => originalMap.get(state) || state)
            .filter(Boolean);
    }, [cartItems]);

    const formatPickupLocationLabel = (location) => {
        const centreAddress = location?.collectionCentre?.address;
        const address = location?.address || location?.street;
        const city = location?.city;
        const state = location?.state;
        return (
            centreAddress ||
            address ||
            [city, state].filter(Boolean).join(", ") ||
            location?._id ||
            "Pickup location"
        );
    };
    const toggleAddressModal = () => {
        if (isAuthenticated) {
            toggleAuthenticatedUserAddressModal();
        } else if (isGuestUser) {
            navigate("/guest-sign-in");
        }
    };
    const buildSplitBuyPayload = useCallback((items) => {
        return (items || []).flat().map((it) => ({
            id: it.split_buy_id || it.id,
            number_of_slots_to_be_purchased: it.fractional_number_of_slots_purchased || 1,
        }));
    }, []);

    // Handlers
    const handleAddressSelect = useCallback(
        async (address) => {
            if (loading || isGuestUser) return;

            if (isPickup) {
                setIsPickup(false);
                setSelectedPickupLocationId("");
                setPickupState("");
                setPickupLocations([]);
                setPickupError(null);
            }

            setSelectedAddress(address);
            setIsAddressModalOpen(false);
            Mixpanel.track("Tap-DeliveryAddressScreen-AddressSelected");

            try {
                setLoading(true);
                const splitBuys = buildSplitBuyPayload(combined_split_buys);

                if (!splitBuys.length) {
                    activateNotification({
                        title: "Error",
                        body: "No items to recombine.",
                    });
                    return;
                }

                const result = await handleCheckout({
                    split_buy: splitBuys,
                    addressIdOverride: address?._id,
                    delivery_state: cost_breakdown.map((item) => item.state).join("|"),
                });
                // activateNotification({
                //          title: result.success ? "Success" : "Error",
                //          body: result.message,
                // });
                if (result.success) {
                    const newCheckoutData = result.data?.data || result.data || result;
                    setCheckoutData(newCheckoutData);
                }
                else if (result.message?.includes("Please add a delivery")) {
                    setIsAddressModalOpen(true);
                }
            } catch (e) {
                activateNotification({
                    title: "Error",
                    body: "Failed to re-combine delivery.",
                });
            } finally {
                setLoading(false);
            }
        },
        [
            loading,
            combined_split_buys,
            buildSplitBuyPayload,
            handleCheckout,
            setIsAddressModalOpen,
        ]
    );

    const toggleReferralModal = useCallback(() => {
        setIsReferralModalOpen((prev) => !prev);
    }, []);

    const handleApplyReferralCode = useCallback(
        async (code) => {
            try {
                setLoading(true);
                const splitBuys = buildSplitBuyPayload(combined_split_buys);

                if (!splitBuys.length) {
                    activateNotification({
                        title: "Error",
                        body: "No items to apply referral code.",
                    });
                    throw new Error("No items available");
                }

                const addressId = split_buyer_address?._id || selectedAddress?._id;
                const result = await handleCheckout({
                    split_buy: splitBuys,
                    addressIdOverride: addressId,
                    pickUpAddressId: isPickup ? selectedPickupLocationId : null,
                    discount_code: code,
                    delivery_state: cost_breakdown.map((item) => item.state).join("|"),
                });

                if (result.success) {
                    const newCheckoutData = result.data?.data || result.data || result;
                    setCheckoutData(newCheckoutData);
                    if (result.discountApplied === false) {
                        setAppliedReferralCode(null);
                        return Promise.resolve();
                    }
                    setAppliedReferralCode(code);
                    activateNotification({
                        title: "Success",
                        body: result.message || "Referral code applied successfully!",
                    });
                    return Promise.resolve();
                } else {
                    activateNotification({
                        title: "Error",
                        body: result.message || "Failed to apply referral code",
                    });
                    throw new Error(result.message || "Failed to apply referral code");
                }
            } catch (error) {
                activateNotification({
                    title: "Error",
                    body: error.message || "Failed to apply referral code",
                });
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [
            combined_split_buys,
            buildSplitBuyPayload,
            handleCheckout,
            split_buyer_address,
            selectedAddress,
            isPickup,
            selectedPickupLocationId,
        ]
    );

    const handleMakePayment = useCallback(async () => {
        if (isStaffCheckout && !staffEmail.trim()) {
            activateNotification({
                title: "Staff email required",
                body: "Enter the staff email before generating the payment link.",
            });
            return;
        }

        const cartItemIds = cartItems.map((item) => item._id);
        Mixpanel.track("Tap-CheckoutCartScreen-MakePaymentButton", {
            split_buy_ids: cartItemIds,
            delivery_state: cost_breakdown.map((item) => item.state).join("|"),
            discount_applied: discount_applied,
            is_pickup: isPickup,
        });

        let paymentUrl = payment_url;
        let paymentCheckoutData = checkoutData;
        if (orderNotes.trim() || isStaffCheckout) {
            try {
                setLoading(true);
                const result = await handleCheckout({
                    split_buy: buildSplitBuyPayload(combined_split_buys),
                    addressIdOverride: isPickup ? null : split_buyer_address?._id || selectedAddress?._id || null,
                    pickUpAddressId: isPickup ? selectedPickupLocationId : null,
                    discount_code: appliedReferralCode || undefined,
                    delivery_state: cost_breakdown.map((item) => item.state).join("|"),
                    order_notes: orderNotes.trim(),
                    staff_email: isStaffCheckout ? staffEmail.trim() : "",
                });
                if (!result.success) {
                    activateNotification({ title: "Error", body: result.message || "Could not save your order note." });
                    return;
                }
                const refreshedCheckout = result.data?.data || result.data || result;
                setCheckoutData(refreshedCheckout);
                paymentCheckoutData = refreshedCheckout;
                paymentUrl = refreshedCheckout?.payment_url || paymentUrl;
            } catch (error) {
                activateNotification({ title: "Error", body: "Could not save your order note." });
                return;
            } finally {
                setLoading(false);
            }
        }

        if (!paymentUrl) {
            activateNotification({
                title: "Error",
                body: "Payment URL unavailable.",
            });
            return;
        }

        if (isStaffCheckout) {
            const firstName = user?.firstname || displayName.split(" ")[0] || "Customer";
            const uniqueNames = [
                ...new Set(
                    (paymentCheckoutData?.combined_split_buys || combined_split_buys)
                        .flat()
                        .map((item) => item?.split_buy_name)
                        .filter(Boolean)
                ),
            ];
            const orderNames = uniqueNames.length <= 1 ? uniqueNames.join(", ") : `${uniqueNames.slice(0, -1).join(", ")} and ${uniqueNames.at(-1)}`
            const paymentBreakdown =
                paymentCheckoutData?.cost_breakdown || cost_breakdown;
            const totalItem =
                paymentBreakdown.find((item) =>
                    item?.label?.toLowerCase().includes("total payment")
                ) ||
                paymentBreakdown.find((item) =>
                    item?.label?.toLowerCase().includes("total")
                );
            const totalAmount = Number(totalItem?.value || 0).toLocaleString(
                "en-NG",
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            );
            const paymentMessage = `Hi ${firstName},\nThanks for your order for ${orderNames || "your selected orders"}!\nYou can complete your order of ₦${totalAmount} securely here: ${paymentUrl} (Standard processing fees apply at checkout).\nWe'll begin processing your order immediately once payment is confirmed. Thanks!`;

            try {
                await copyToClipboard(paymentMessage);
                activateNotification({
                    title: "Payment message copied",
                    body: "The payment details are ready to send to the customer.",
                });
            } catch {
                activateNotification({
                    title: "Could not copy payment message",
                    body: "Please allow clipboard access and try again.",
                });
            }
            return;
        }

        sessionStorage.setItem(
            "pending_payment_cart_items",
            JSON.stringify(cartItemIds)
        );
        window.location.href = paymentUrl;
    }, [appliedReferralCode, buildSplitBuyPayload, cartItems, checkoutData, combined_split_buys, cost_breakdown, discount_applied, displayName, handleCheckout, isPickup, isStaffCheckout, orderNotes, payment_url, selectedAddress, selectedPickupLocationId, split_buyer_address, staffEmail, user]);

    // Effects
    useEffect(() => {
        if (checkoutData) {
            sessionStorage.setItem(
                CHECKOUT_DATA_STORAGE_KEY,
                JSON.stringify(checkoutData)
            );
            return;
        }

        if (!checkoutData) {
            navigate("/cart", { replace: true });
        }
    }, [checkoutData, navigate]);

    useEffect(() => {
        Mixpanel.track("View-CheckoutScreen");
    }, []);

    useEffect(() => {
        const pickupId =
            checkoutData?.pick_up_address_id ||
            checkoutData?.pick_up_location_id ||
            user?.pick_up_location_id ||
            user?.pick_up_address_id ||
            null;


        if (!autoPickupInitialized && pickupId) {
            setIsPickup(true);
            setSelectedPickupLocationId(pickupId);
            setAutoPickupInitialized(true);
            return;
        }

        if (!isPickup) {
            setPickupState("");
            setPickupLocations([]);
            setPickupError(null);
            setSelectedPickupLocationId("");
            return;
        }

        if (availablePickupStates.length === 1) {
            setPickupState((prev) => prev || availablePickupStates[0]);
        } else if (pickupState && !availablePickupStates.includes(pickupState)) {
            setPickupState("");
        }
    }, [
        autoPickupInitialized,
        checkoutData,
        user,
        isPickup,
        availablePickupStates,
        pickupState,
    ]);

    useEffect(() => {
        const fetchPickupLocations = async () => {
            if (!isPickup || !pickupState) {
                setPickupLocations([]);
                setPickupError(null);
                return;
            }

            try {
                setPickupLoading(true);
                setPickupError(null);
                const { data } = await api.get(
                    `/get_agrogrid_delivery_locations_by_state?state=${encodeURIComponent(
                        pickupState
                    )}`
                );
                const locations = Array.isArray(data?.data)
                    ? data.data
                    : Array.isArray(data?.locations)
                        ? data.locations
                        : Array.isArray(data)
                            ? data
                            : [];
                setPickupLocations(locations);

                if (!locations.length) {
                    setPickupError(
                        "No pickup locations available for this state. Please choose delivery."
                    );
                    setSelectedPickupLocationId("");
                }
            } catch (error) {
                const message =
                    error?.response?.data?.message ||
                    "Unable to load pickup locations for this state.";
                setPickupError(message);
                setPickupLocations([]);
                setSelectedPickupLocationId("");
            } finally {
                setPickupLoading(false);
            }
        };

        fetchPickupLocations();
    }, [api, isPickup, pickupState]);

    useEffect(() => {
        const fetchAllPickupLocations = async () => {
            if (!isPickup || !selectedPickupLocationId) return;
            if (pickupLocations?.length) return;

            try {
                const { data } = await api.get("/get_agrogrid_delivery_locations");
                const locations = Array.isArray(data?.data)
                    ? data.data
                    : Array.isArray(data?.locations)
                        ? data.locations
                        : Array.isArray(data)
                            ? data
                            : [];
                setPickupLocations(locations);
            } catch {
            }
        };

        fetchAllPickupLocations();
    }, [api, isPickup, selectedPickupLocationId, pickupLocations]);

    const handlePickupSelectionChange = useCallback(
        async (pickupId) => {
            if (!isAuthenticated) return;
            setSelectedPickupLocationId(pickupId);
            setPickupError(null);
            if (!pickupId) return;

            setLoading(true);
            try {
                const splitBuys = buildSplitBuyPayload(combined_split_buys);
                if (!splitBuys.length) {
                    const message = "No items to update for pickup.";
                    activateNotification({ title: "Error", body: message });
                    setPickupError(message);
                    return;
                }

                const result = await handleCheckout({
                    split_buy: splitBuys,
                    pickUpAddressId: pickupId,
                    delivery_state: cost_breakdown.map((item) => item.state).join("|"),
                    discount_code: appliedReferralCode,
                });

                // activateNotification({
                //   title: result.success ? "Success" : "Error",
                //   body: result.message,
                // });

                if (result.success) {
                    const newCheckoutData = result.data?.data || result.data || result;
                    setCheckoutData(newCheckoutData);
                } else {
                    setPickupError(result.message || "Unable to set pickup location.");
                }
            } catch (error) {
                setPickupError("Failed to update pickup location.");
                activateNotification({
                    title: "Error",
                    body: "Failed to set pickup location.",
                });
            } finally {
                setLoading(false);
            }
        },
        [
            appliedReferralCode,
            buildSplitBuyPayload,
            combined_split_buys,
            cost_breakdown,
            handleCheckout,
            isAuthenticated,
        ]
    );

    const handlePickupToggle = useCallback(
        async (checked) => {
            setIsPickup(checked);
            if (checked) return;

            setSelectedPickupLocationId("");
            setPickupState("");
            setPickupLocations([]);
            setPickupError(null);

            const splitBuys = buildSplitBuyPayload(combined_split_buys);
            if (!splitBuys.length) {
                activateNotification({
                    title: "Error",
                    body: "No items to recombine.",
                });
                return;
            }

            try {
                setLoading(true);
                const result = await handleCheckout({
                    split_buy: splitBuys,
                    delivery_state: cost_breakdown.map((item) => item.state).join("|"),
                    discount_code: appliedReferralCode,
                });


                if (result.success) {
                    const newCheckoutData = result.data?.data || result.data || result;
                    setCheckoutData(newCheckoutData);
                }
            } catch (error) {
                activateNotification({
                    title: "Error",
                    body: "Failed to update checkout for delivery.",
                });
            } finally {
                setLoading(false);
            }
        },
        [
            appliedReferralCode,
            buildSplitBuyPayload,
            combined_split_buys,
            cost_breakdown,
            handleCheckout,
        ]
    );

    return {
        checkoutData,
        displayName,
        user,
        isAuthenticated,
        isGuestUser,
        navigate,

        // address / delivery
        selectedAddress,
        split_buyer_address,
        isAddressModalOpen,
        setIsAddressModalOpen,
        toggleAddressModal,
        handleAddressSelect,

        // pickup
        isPickup,
        pickupState,
        setPickupState,
        pickupLocations,
        pickupLoading,
        pickupError,
        setPickupError,
        selectedPickupLocationId,
        setSelectedPickupLocationId,
        availablePickupStates,
        handlePickupToggle,
        handlePickupSelectionChange,
        formatPickupLocationLabel,

        // checkout data / order summary
        combined_split_buys,
        cost_breakdown,
        payment_url,
        discount_applied,
        orderNotes,
        setOrderNotes,
        isStaffCheckout,
        staffEmail,
        setStaffEmail,

        // referral
        appliedReferralCode,
        isReferralModalOpen,
        toggleReferralModal,
        handleApplyReferralCode,

        // payment
        handleMakePayment,

        // loading state
        loading,
    };
}
